import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { ConfirmDeletionModal } from '../../components/ConfirmDeletionModal';
import { useAuth } from '../../components/auth/AuthProvider';
import EventCreateModal, { EVENT_TYPE_OPTIONS } from '../../components/events/EventCreateModal';
import { deleteEvent } from '../../controllers/events';
import { downloadAttendeesCsv, listEvents } from '../../controllers/events';
import { getOrg } from '../../controllers/org';

const visibilityOptions = [
  { value: 1, label: 'Nacional' },
  { value: 2, label: 'Regional' },
  { value: 3, label: 'Diocesis' },
  { value: 4, label: 'Comunidad' },
];

function EventsAdmin() {
  const { token, hasPermission } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [okMessage, setOkMessage] = useState('');
  const [org, setOrg] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingEvent, setDeletingEvent] = useState(null);
  const [filters, setFilters] = useState({
    cycleYear: new Date().getFullYear(),
    eventType: '',
    statusFilter: '',
  });

  const canManage = useMemo(() => {
    return [
      'eventos_admin_nacional',
      'eventos_admin_region',
      'eventos_admin_diocesis',
      'eventos_admin_comunidad',
    ].some(permission => hasPermission(permission));
  }, [hasPermission]);

  const canExport = useMemo(() => hasPermission('eventos_reportes') || canManage, [hasPermission, canManage]);

  const createScope = useMemo(() => {
    if (!token) {
      return {};
    }

    try {
      const decoded = JSON.parse(atob(token.split('.')[1]));
      const roles = decoded?.authInfo?.roles || [];
      const membership = decoded?.authInfo?.membership || {};
      const highestRole = [...roles].sort((a, b) => parseInt(a.level) - parseInt(b.level))[0];

      if (!highestRole?.level) {
        return {};
      }

      if (highestRole.level === 1) {
        return {
          fixedVisibilityLevel: 1,
        };
      }
      if (highestRole.level === 2) {
        return {
          fixedVisibilityLevel: 2,
          fixedRegionId: membership.region_id,
        };
      }
      if (highestRole.level === 3) {
        return {
          fixedVisibilityLevel: 3,
          fixedRegionId: membership.region_id,
          fixedDiocesisId: membership.diocesis_id,
        };
      }
      if (highestRole.level === 4) {
        return {
          fixedVisibilityLevel: 4,
          fixedRegionId: membership.region_id,
          fixedDiocesisId: membership.diocesis_id,
          fixedComunidadId: membership.comunidad_id,
        };
      }

      return {};
    }
    catch (_e) {
      return {};
    }
  }, [token]);

  useEffect(() => {
    document.title = 'FAMA - Eventos';
  }, []);

  useEffect(() => {
    loadEvents();
    loadOrgData();
  }, []);

  async function loadOrgData() {
    try {
      const data = await getOrg();
      setOrg(data);
    }
    catch (_e) {
      setOrg({ regiones: [], diocesis: [], comunidades: [] });
    }
  }

  async function loadEvents() {
    setLoading(true);
    setError('');
    try {
      const rows = await listEvents(token, filters);
      setEvents(Array.isArray(rows) ? rows : []);
    }
    catch (e) {
      setError(e.message || 'No se pudieron cargar eventos.');
    }
    finally {
      setLoading(false);
    }
  }

  async function handleCreatedEvent() {
    setOkMessage('Evento creado correctamente.');
    await loadEvents();
  }

  async function handleDownloadCsv() {
    setDownloading(true);
    setError('');
    try {
      await downloadAttendeesCsv(token, filters);
    }
    catch (e) {
      setError(e.message || 'No se pudo descargar reporte CSV.');
    }
    finally {
      setDownloading(false);
    }
  }

  function requestDeleteEvent(event) {
    setDeletingEvent(event);
    setShowDeleteModal(true);
  }

  async function confirmDeleteEvent() {
    if (!deletingEvent) {
      return;
    }

    try {
      await deleteEvent(token, deletingEvent.event_id);
      setEvents((prev) => prev.filter((item) => item.event_id !== deletingEvent.event_id));
      setOkMessage('Evento eliminado correctamente.');
      setShowDeleteModal(false);
      setDeletingEvent(null);
    }
    catch (e) {
      setError(e.message || 'No se pudo eliminar el evento.');
    }
  }

  return (
    <Container className="py-4">
      <Row className="mb-3">
        <Col>
          <h1>Eventos</h1>
          <p className="text-muted mb-0">Administracion de eventos por nivel, registro y reportes CSV.</p>
        </Col>
        {canManage && (
          <Col md="auto" className="d-flex align-items-start">
            <Button onClick={() => setShowCreateModal(true)}>Crear evento</Button>
          </Col>
        )}
      </Row>

      {!!error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {!!okMessage && <Alert variant="success" onClose={() => setOkMessage('')} dismissible>{okMessage}</Alert>}

      <Card className="mb-4">
        <Card.Header><strong>Filtros</strong></Card.Header>
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Ciclo</Form.Label>
                <Form.Control
                  type="number"
                  value={filters.cycleYear}
                  onChange={e => setFilters(prev => ({ ...prev, cycleYear: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Tipo de evento</Form.Label>
                <Form.Select
                  value={filters.eventType}
                  onChange={e => setFilters(prev => ({ ...prev, eventType: e.target.value }))}
                >
                  <option value="">Todos</option>
                  {EVENT_TYPE_OPTIONS.map((option) => <option key={`event-type-${option}`} value={option}>{option}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Estatus</Form.Label>
                <Form.Select
                  value={filters.statusFilter}
                  onChange={e => setFilters(prev => ({ ...prev, statusFilter: e.target.value }))}
                >
                  <option value="">Todos</option>
                  <option value="upcoming">Programados</option>
                  <option value="past">Pasados</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex gap-2">
              <Button onClick={loadEvents}>Aplicar</Button>
              {canExport && (
                <Button variant="outline-secondary" disabled={downloading} onClick={handleDownloadCsv}>
                  {downloading ? 'Descargando...' : 'CSV'}
                </Button>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {canManage && (
        <EventCreateModal
          show={showCreateModal}
          onHide={() => setShowCreateModal(false)}
          token={token}
          org={org}
          defaultCycleYear={parseInt(filters.cycleYear) || new Date().getFullYear()}
          fixedVisibilityLevel={createScope.fixedVisibilityLevel}
          fixedRegionId={createScope.fixedRegionId}
          fixedDiocesisId={createScope.fixedDiocesisId}
          fixedComunidadId={createScope.fixedComunidadId}
          modalTitle="Crear evento"
          onCreated={handleCreatedEvent}
        />
      )}

      <Card>
        <Card.Header className="d-flex align-items-center justify-content-between">
          <strong>Eventos registrados</strong>
          <Badge bg="secondary">{events.length}</Badge>
        </Card.Header>
        <Card.Body style={{ overflowX: 'auto' }}>
          {loading ? (
            <div className="text-center py-4"><Spinner /></div>
          ) : (
            <Table striped hover>
              <thead>
                <tr>
                  <th>Titulo</th>
                  <th>Tipo</th>
                  <th>Nivel</th>
                  <th>Inicio</th>
                  <th>Fin</th>
                  <th>Pago</th>
                  <th>Cupo</th>
                  <th>Registros</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={`event-row-${event.event_id}`}>
                    <td>
                      <Link to={`/app/admin/eventos/${event.event_id}`}>{event.title}</Link>
                    </td>
                    <td>{event.event_type}</td>
                    <td>{visibilityOptions.find(v => v.value === event.visibility_level)?.label || event.visibility_level}</td>
                    <td>{event.starts_on}</td>
                    <td>{event.ends_on}</td>
                    <td>{event.payment_type}</td>
                    <td>{event.capacity_limit || '-'}</td>
                    <td>{event.event_registrations?.length || 0}</td>
                    <td>
                      {!(event.event_registrations?.length) && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => requestDeleteEvent(event)}
                        >
                          Eliminar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {!events.length && (
                  <tr>
                    <td colSpan={9} className="text-center text-muted">No hay eventos para estos filtros.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <ConfirmDeletionModal
        show={showDeleteModal}
        handleClose={() => {
          setShowDeleteModal(false);
          setDeletingEvent(null);
        }}
        title="Eliminar evento"
        triggerDelete={confirmDeleteEvent}
        body={deletingEvent ? (
          <>
            <p>Vas a eliminar el evento <strong>{deletingEvent.title}</strong>.</p>
            <p>Solo puedes continuar si no tiene registros.</p>
            <p>Esta acción no podrá deshacerse.</p>
          </>
        ) : null}
      />
    </Container>
  );
}

export default EventsAdmin;
